<script setup lang="ts">
import { PasswordRequirements, VButton, VLoadingSection, VPasswordField, VTextField } from '@/modules/shared/components'
import InputCheckbox from '@/modules/shared/components/InputCheckbox.vue'
import useVuelidate from '@vuelidate/core'
import { helpers, required, sameAs } from '@vuelidate/validators'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/modules/public/stores/user-store'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useWorkspaceStore } from '@/modules/public/stores/workspace-store'
import AuthLayout from '@/modules/auth/layouts/auth-layout.vue'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import FormError from '@/modules/auth/components/form-error.vue'

const route = useRoute()
const router = useRouter()
const { slug } = route.params as { slug: string }

const userStore = useUserStore()
const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()
const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

const requiredTrue = helpers.withMessage('This field must be checked.', (value: boolean) => value === true)
const workspace = computed(() => workspaceStore.workspace)
const hasMembershipAgreement = computed(() => !!workspace.value?.membership_agreement)

const loading = ref(false)
const apply_w_token = ref(false)
const passwordRequirementsValid = ref(false)
const passwordSubmitAttempted = ref(false)
const tokenPasswordRequirementsValid = ref(false)
const tokenPasswordSubmitAttempted = ref(false)

const isGroupApplication = computed(() => route.name === 'public.group-users.apply')

// Registration Token
const registrationTokenForm = ref({
  registration_token: null,
  password: null,
  password_confirmation: null,
})
const registrationTokenRules = {
  registration_token: { required },
  password: { required },
  password_confirmation: { required, sameAsRepeat: sameAs(computed(() => registrationTokenForm.value.password)) },
}
const vrt$ = useVuelidate(registrationTokenRules, registrationTokenForm, { $lazy: true })
const submitRegistrationToken = async () => {
  tokenPasswordSubmitAttempted.value = true
  const valid = await vrt$.value.$validate()
  if (!valid || !tokenPasswordRequirementsValid.value) return

  loading.value = true
  userStore.clearErrorMessage()
  await userStore.createInvestorUserWithToken(registrationTokenForm.value, { slug })
  loading.value = false
}

// FORM
const form = ref({
  first_name: null,
  last_name: null,
  password: null,
  password_confirmation: null,
  is_agreement_accepted: false,
  is_group_membership_agreement_accepted: false,
})
const rules = computed(() => ({
  first_name: { required },
  last_name: { required },
  password: { required },
  password_confirmation: { required, sameAsRepeat: sameAs(computed(() => form.value.password)) },
  is_agreement_accepted: { requiredTrue },
  ...(hasMembershipAgreement.value && {
    is_group_membership_agreement_accepted: { requiredTrue },
  }),
}))
const v$ = useVuelidate(rules, form, { $lazy: true })

const submitForm = async () => {
  passwordSubmitAttempted.value = true
  const valid = await v$.value.$validate()
  if (!valid || !passwordRequirementsValid.value) return

  loading.value = true
  userStore.clearErrorMessage()
  const payload = {
    ...form.value,
    name: `${form.value.first_name} ${form.value.last_name}`,
    is_group_membership_agreement_accepted: form.value.is_group_membership_agreement_accepted,
  }
  await userStore.createInvestorUser(payload, { slug })
  loading.value = false
}

onMounted(async () => {
  showSkeleton()
  await Promise.all([authStore.refresh(null, route), workspaceStore.fetchWorkspace(slug)])

  if (authStore.isInvestor) {
    const baseUrl = import.meta.env.VITE_VENTURE360_REDIRECT_URL
    const slugParam = slug ? `slug=${slug}` : ''
    const queryParams = [slugParam].filter((param) => param).join('&')

    window.location.href = `${baseUrl}/user?${queryParams}`
    return
  }

  form.value.first_name = authStore.current_user?.first_name || null
  form.value.last_name = authStore.current_user?.last_name || null

  hideSkeleton()
})
</script>

<template>
  <AuthLayout>
    <template v-slot:icon>
      <svg width="72" height="96" viewBox="0 0 72 96" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M63 39H9C5.68629 39 3 41.6863 3 45V87C3 90.3137 5.68629 93 9 93H63C66.3137 93 69 90.3137 69 87V45C69 41.6863 66.3137 39 63 39Z"
          fill="#111827"
          stroke="#111827"
          stroke-width="6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M15 39V24C15 12.4 24.4 3 36 3C47.56 3 57 12.4 57 24V39"
          stroke="#111827"
          stroke-width="6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path d="M36 60V72" stroke="white" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </template>

    <div class="mx-32 w-full" v-if="skeleton">
      <VLoadingSection title="Hang tight!" description="Your form is being set up and will appear shortly." />
    </div>
    <div class="mx-32 w-full" v-else>
      <h2 class="mb-7 text-2xl font-bold leading-5 text-gray-900 dark:text-gray-300 sm:text-3xl sm:leading-7">
        Create {{ isGroupApplication ? 'Group User' : 'Investor' }} Account
      </h2>
      <div class="mb-5">Please complete the information below to set-up your account.</div>
      <div class="mb-10 cursor-pointer text-blue-400" @click="apply_w_token = !apply_w_token">
        {{ apply_w_token ? 'Are you a new investor?' : 'Do you have registration token?' }}
      </div>
      <FormError :error="userStore.errorMessage" v-show="userStore.errorMessage !== null" />
      <template v-if="apply_w_token">
        <form @submit.prevent="submitRegistrationToken" class="mb-12">
          <div class="space-y-5">
            <VTextField
              v-model="registrationTokenForm.registration_token"
              label="Enter Registration Token"
              placeholder="example: first-name-a2f3d9b1c8e0f8a74"
              property="registration_token"
              :v$="vrt$"
            />
            <div>
              <VPasswordField
                v-model="registrationTokenForm.password"
                label="Password"
                placeholder="Password"
                property="password"
                :v$="vrt$"
              />
              <PasswordRequirements
                :password="registrationTokenForm.password"
                :show-errors="tokenPasswordSubmitAttempted"
                @update:valid="tokenPasswordRequirementsValid = $event"
              />
            </div>
            <VPasswordField
              v-model="registrationTokenForm.password_confirmation"
              label="Confirm Password"
              placeholder="Confirm Password"
              property="password_confirmation"
              :v$="vrt$"
            />
          </div>
        </form>
        <VButton
          :click="submitRegistrationToken"
          :loading="loading"
          class="w-full !bg-[#3D8A9D] font-semibold"
          variant="primary"
          size="xl"
        >
          Register
        </VButton>
      </template>
      <template v-else>
        <form @submit.prevent="submitForm" class="mb-12">
          <div class="space-y-5">
            <VTextField
              v-model="form.first_name"
              label="First Name"
              placeholder="First Name"
              property="first_name"
              :v$="v$"
            />
            <VTextField
              v-model="form.last_name"
              label="Last Name"
              placeholder="Last Name"
              property="last_name"
              :v$="v$"
            />
            <div>
              <VPasswordField
                v-model="form.password"
                label="Password"
                placeholder="Password"
                property="password"
                :v$="v$"
              />
              <PasswordRequirements
                :password="form.password"
                :show-errors="passwordSubmitAttempted"
                @update:valid="passwordRequirementsValid = $event"
              />
            </div>
            <VPasswordField
              v-model="form.password_confirmation"
              label="Confirm Password"
              placeholder="Confirm Password"
              property="password_confirmation"
              :v$="v$"
            />
          </div>

          <label class="mt-6 flex cursor-pointer select-none items-center gap-3 text-sm">
            <InputCheckbox v-model="form.is_agreement_accepted" />
            <span>
              I agree to the
              <a href="https://www.venture360.co/terms" target="_blank" class="hyperlink">Terms of Service ↗</a>
              and
              <a href="https://www.venture360.co/privacy" target="_blank" class="hyperlink">Privacy Policy ↗</a>
            </span>
          </label>

          <label v-if="hasMembershipAgreement" class="mt-4 flex cursor-pointer select-none items-center gap-3 text-sm">
            <InputCheckbox v-model="form.is_group_membership_agreement_accepted" />
            <span>
              I agree to the
              <a :href="workspace.membership_agreement.url" target="_blank" class="hyperlink">Investor Agreement ↗</a>
            </span>
          </label>
        </form>
        <VButton
          :click="submitForm"
          :loading="loading"
          :disabled="
            !form.is_agreement_accepted || (hasMembershipAgreement && !form.is_group_membership_agreement_accepted)
          "
          class="w-full !bg-[#3D8A9D] font-semibold"
          variant="primary"
          size="xl"
        >
          Create Account
        </VButton>
      </template>

      <div class="mt-10 text-center text-sm">
        Already have an account?
        <RouterLink :to="{ name: 'login', query: { account: slug } }" class="ml-1 font-semibold text-[#3D8A9D]">
          Login
        </RouterLink>
      </div>
    </div>
  </AuthLayout>
</template>
